import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineContent,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector
} from '@mui/lab';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  EventAvailable as EventIcon,
  Mail as MailIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  EventNote as EventNoteIcon,
  CardMembership as MembershipIcon
} from '@mui/icons-material';
import { db } from '../../../../firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import EventAttendanceList from './EventAttendanceList';

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-detail-tabpanel-${index}`}
      aria-labelledby={`contact-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Event Attendance interface
interface EventAttendance {
  eventId?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  ticketType?: string;
  status?: string;
  attendeeId?: string;
  importDate: string;
  notes?: string;
}

// Contact interface
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
  notes?: string;
  engagementScore: number;
  lastContactDate?: Date;
  source: string;
  createdAt: Date;
  consentStatus: 'granted' | 'denied' | 'unknown';
  // Event fields
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  importDate?: string;
  // Multi-event attendance
  eventAttendance?: EventAttendance[];
}

// Engagement event interface
interface EngagementEvent {
  id: string;
  contactId: string;
  type: 'email_sent' | 'email_opened' | 'event_invite' | 'event_attended' | 'subscription' | 'message' | 'form_submission' | 'note';
  timestamp: Date;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
}

const ContactDetail: React.FC = () => {
  const { organizationId, contactId } = useParams<{ organizationId: string; contactId: string }>();
  const navigate = useNavigate();
  
  // State
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedContact, setEditedContact] = useState<Partial<Contact>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [events, setEvents] = useState<EventAttendance[]>([]);
  const [contactRefreshTrigger, setContactRefreshTrigger] = useState(0);
  const [engagementEvents, setEngagementEvents] = useState<EngagementEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);

  // Initialize edited contact
  useEffect(() => {
    if (contact) {
      setEditedContact({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone || '',
        company: contact.company || '',
        title: contact.title || '',
        tags: [...(contact.tags || [])],
        notes: contact.notes || '',
      });
      
      // Initialize events from eventAttendance field or create from single event
      if (contact.eventAttendance && contact.eventAttendance.length > 0) {
        setEvents(contact.eventAttendance);
      } else if (contact.eventName) {
        // Create a single event from the legacy fields
        const singleEvent: EventAttendance = {
          eventName: contact.eventName || 'Unknown Event',
          eventDate: contact.eventDate || new Date().toISOString().split('T')[0],
          eventDescription: contact.eventDescription || '',
          importDate: contact.importDate || new Date().toISOString(),
          status: 'Confirmed'
        };
        setEvents([singleEvent]);
      } else {
        setEvents([]);
      }
    }
  }, [contact]);

  // Fetch contact data
  const fetchContact = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!contactId || !organizationId) {
        throw new Error('Missing contact or organization ID');
      }
      
      const contactDoc = await getDoc(doc(db, `organizations/${organizationId}/contacts/${contactId}`));
      
      if (!contactDoc.exists()) {
        throw new Error('Contact not found');
      }
      
      const data = contactDoc.data();
      
      // Convert Timestamp objects to Date
      const createdAt = data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date();
      const lastContactDate = data.lastContactDate ? new Date(data.lastContactDate.seconds * 1000) : undefined;
      
      setContact({
        id: contactDoc.id,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        company: data.company || '',
        title: data.title || '',
        tags: Array.isArray(data.tags) ? data.tags : [],
        notes: data.notes || '',
        engagementScore: typeof data.engagementScore === 'number' ? data.engagementScore : 0,
        lastContactDate,
        source: data.source || 'manual',
        createdAt,
        consentStatus: data.consentStatus || 'unknown',
        eventName: data.eventName || '',
        eventDate: data.eventDate || '',
        eventDescription: data.eventDescription || '',
        importDate: data.importDate || '',
        eventAttendance: data.eventAttendance || []
      });
    } catch (err) {
      console.error('Error fetching contact:', err);
      setError(`Failed to load contact details: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch engagement events
  useEffect(() => {
    if (!organizationId || !contactId) return;
    
    const fetchEngagementEvents = async () => {
      setEventsLoading(true);
      try {
        const eventsRef = collection(db, `organizations/${organizationId}/engagement_events`);
        const eventsQuery = query(
          eventsRef,
          where('contactId', '==', contactId),
          orderBy('timestamp', 'desc')
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        
        const events: EngagementEvent[] = eventsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            contactId: data.contactId,
            type: data.type,
            timestamp: new Date(data.timestamp.seconds * 1000),
            title: data.title,
            description: data.description,
            metadata: data.metadata
          };
        });
        
        setEngagementEvents(events);
      } catch (error) {
        console.error('Error fetching engagement events:', error);
      } finally {
        setEventsLoading(false);
      }
    };
    
    fetchEngagementEvents();
  }, [organizationId, contactId]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle delete contact
  const handleDeleteContact = async () => {
    if (!organizationId || !contactId) return;
    
    try {
      await deleteDoc(doc(db, `organizations/${organizationId}/contacts/${contactId}`));
      navigate(`/organizations/${organizationId}/contacts`);
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Failed to delete contact');
    }
  };

  // Handle edit mode
  const handleEditModeToggle = () => {
    setEditMode(!editMode);
  };

  // Handle input change in edit mode
  const handleInputChange = (field: keyof Contact, value: any) => {
    if (!editedContact) return;
    
    setEditedContact({
      ...editedContact,
      [field]: value
    });
  };

  // Handle save edited contact
  const handleSaveContact = async () => {
    if (!organizationId || !contactId || !editedContact) return;
    
    try {
      const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
      
      // Prepare data for update
      const updateData = {
        firstName: editedContact.firstName,
        lastName: editedContact.lastName,
        email: editedContact.email,
        phone: editedContact.phone || '',
        company: editedContact.company || '',
        title: editedContact.title || '',
        tags: editedContact.tags || [],
        notes: editedContact.notes || '',
        lastUpdated: Timestamp.now()
      };
      
      await updateDoc(contactRef, updateData);
      
      // Update local state
      setContact({
        ...contact!,
        ...editedContact
      });
      
      setEditMode(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      setError('Failed to update contact');
    }
  };

  // Get contact fullname
  const getFullName = () => {
    if (!contact) return '';
    return `${contact.firstName} ${contact.lastName}`.trim();
  };

  // Format date for display
  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get icon for engagement event type
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'email_sent':
        return <MailIcon color="primary" />;
      case 'email_opened':
        return <MailIcon color="success" />;
      case 'event_invite':
        return <EventIcon color="secondary" />;
      case 'event_attended':
        return <EventIcon color="success" />;
      case 'subscription':
        return <MembershipIcon color="primary" />;
      case 'message':
        return <EmailIcon color="info" />;
      case 'form_submission':
        return <EventNoteIcon color="warning" />;
      case 'note':
        return <EventNoteIcon color="inherit" />;
      default:
        return <EventNoteIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/organizations/${organizationId}/contacts`)}
        >
          Back to Contacts
        </Button>
      </Box>
    );
  }

  if (!contact) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Contact not found</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/organizations/${organizationId}/contacts`)}
        >
          Back to Contacts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with navigation */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/organizations/${organizationId}/contacts`)}
          sx={{ mr: 2 }}
        >
          Back to Contacts
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Contact Details
        </Typography>
        <Box>
          {editMode ? (
            <>
              <Button 
                variant="outlined" 
                onClick={handleEditModeToggle}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSaveContact}
                disabled={!editedContact?.firstName || !editedContact?.email}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <IconButton 
                color="primary" 
                onClick={handleEditModeToggle}
                sx={{ mr: 1 }}
              >
                <EditIcon />
              </IconButton>
              <IconButton 
                color="error" 
                onClick={() => setDeleteDialogOpen(true)}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Contact Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{ 
                  width: 100, 
                  height: 100, 
                  fontSize: '2rem',
                  bgcolor: 'primary.main',
                  mb: 2
                }}
              >
                {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
              </Avatar>
              
              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editedContact?.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editedContact?.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={editedContact?.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={editedContact?.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={editedContact?.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={editedContact?.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tags (comma separated)"
                      value={editedContact?.tags?.join(', ') || ''}
                      onChange={(e) => handleInputChange('tags', 
                        e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                      )}
                      margin="normal"
                      helperText="Separate tags with commas"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={editedContact?.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      margin="normal"
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Typography variant="h5" align="center" gutterBottom>
                    {getFullName()}
                  </Typography>
                  
                  {contact.title && contact.company && (
                    <Typography variant="body1" color="text.secondary" align="center" gutterBottom>
                      {contact.title} at {contact.company}
                    </Typography>
                  )}
                  
                  {contact.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5, mt: 1, mb: 2 }}>
                      {contact.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                  
                  <Divider sx={{ width: '100%', my: 2 }} />
                  
                  <List sx={{ width: '100%' }}>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={contact.email} 
                      />
                    </ListItem>
                    
                    {contact.phone && (
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Phone" 
                          secondary={contact.phone} 
                        />
                      </ListItem>
                    )}
                    
                    {contact.company && (
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Company" 
                          secondary={contact.company} 
                        />
                      </ListItem>
                    )}
                    
                    {contact.title && (
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Job Title" 
                          secondary={contact.title} 
                        />
                      </ListItem>
                    )}
                    
                    <ListItem>
                      <ListItemIcon>
                        <EventIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Added On" 
                        secondary={formatDate(contact.createdAt)} 
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <LinkIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Source" 
                        secondary={contact.source} 
                      />
                    </ListItem>
                  </List>
                  
                  {contact.notes && (
                    <>
                      <Divider sx={{ width: '100%', my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {contact.notes}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Tabs Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ width: '100%' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="contact detail tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Engagement Timeline" />
              <Tab label="Activity Metrics" />
              <Tab label="Related Entities" />
              <Tab label="Events" />
            </Tabs>
            
            {/* Engagement Timeline Tab */}
            <TabPanel value={tabValue} index={0}>
              {eventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : engagementEvents.length > 0 ? (
                <Timeline position="alternate">
                  {engagementEvents.map((event) => (
                    <TimelineItem key={event.id}>
                      <TimelineSeparator>
                        <TimelineDot color="primary">
                          {getEventIcon(event.type)}
                        </TimelineDot>
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={1} sx={{ p: 2 }}>
                          <Typography variant="subtitle1">{event.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(event.timestamp)}
                          </Typography>
                          {event.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {event.description}
                            </Typography>
                          )}
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    No engagement history found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This contact hasn't interacted with any events or communications yet
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Activity Metrics Tab */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                Engagement Metrics
              </Typography>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1">
                  Activity metrics visualization will appear here
                </Typography>
              </Box>
            </TabPanel>
            
            {/* Related Entities Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                Related Entities
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Events attended, subscriptions, and other linked entities
              </Typography>
            </TabPanel>
            
            {/* Events Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ mb: 3 }}>
                <EventAttendanceList
                  contactId={contactId || ''}
                  organizationId={organizationId || ''}
                  events={events}
                  onUpdate={() => {
                    // Trigger a refresh of the contact data
                    setContactRefreshTrigger(prev => prev + 1);
                  }}
                />
              </Box>
              
              {/* Legacy Event Info */}
              {contact?.eventName && !contact.eventAttendance?.length && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This contact has legacy event information that should be migrated to the new event attendance system.
                  </Alert>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={async () => {
                      if (!contact || !contactId || !organizationId) return;
                      
                      try {
                        // Create a new event attendance record from legacy data
                        const legacyEvent: EventAttendance = {
                          eventName: contact.eventName || 'Unknown Event',
                          eventDate: contact.eventDate || new Date().toISOString().split('T')[0],
                          eventDescription: contact.eventDescription || '',
                          importDate: contact.importDate || new Date().toISOString(),
                          status: 'Confirmed'
                        };
                        
                        // Update the contact document
                        const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
                        await updateDoc(contactRef, {
                          eventAttendance: [legacyEvent]
                        });
                        
                        // Refresh contact data
                        setContactRefreshTrigger(prev => prev + 1);
                      } catch (error) {
                        console.error('Error migrating event data:', error);
                      }
                    }}
                  >
                    Migrate to New Format
                  </Button>
                </Box>
              )}
            </TabPanel>
          </Paper>
          
          {/* Quick Actions */}
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                startIcon={<EmailIcon />}
                size="small"
              >
                Send Email
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<EventIcon />}
                size="small"
              >
                Invite to Event
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<PeopleIcon />}
                size="small"
              >
                Add to Group
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<MembershipIcon />}
                size="small"
              >
                Manage Subscriptions
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {getFullName()}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteContact} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactDetail;
